import React, { useState, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  Alert,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import { useGetAllRoleList, useGetRoleActions, useDeleteBulkRoleActions } from "../../../hooks";
import { Loading, PermissionStatus } from "../../atoms";
import { Table } from "../../molecules";
import { useSetSnackBar } from "../../../store/Snackbar.state";

function RenderStatus(props: any) {
  const { value } = props;
  return <PermissionStatus canCall={value} />;
}

export const BulkDeleteTab: React.FC = () => {
  const [selectedRoleId, setSelectedRoleId] = useState<number | "">("");
  const [roleActions, setRoleActions] = useState<any[]>([]);
  const [selectedActionIds, setSelectedActionIds] = useState<GridRowSelectionModel>([]);
  const [queryOptions, setQueryOptions] = useState({});
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { data: rawRoleList, isLoading: rolesLoading } = useGetAllRoleList("ACTIVE");
  const { mutate: getRoleActions, isLoading: getRoleActionsLoading } = useGetRoleActions();
  const { mutate: deleteBulkRoleActions, isLoading: deleteLoading } = useDeleteBulkRoleActions();
  const setSnackbar = useSetSnackBar();

  const roleList = useMemo(() => {
    return Array.isArray(rawRoleList?.data)
      ? rawRoleList.data.map((role: { name: string; id: number }) => ({
          label: role.name,
          value: role.id,
        }))
      : [];
  }, [rawRoleList?.data]);

  const handleRoleChange = (event: SelectChangeEvent<number>) => {
    const roleId = event.target.value as number;
    setSelectedRoleId(roleId);
    setSelectedActionIds([]);
    
    if (roleId) {
      getRoleActions(
        { roleId },
        {
          onSuccess: (data) => {
            if (data.isSuccess) {
              setRoleActions(data.data || []);
            } else {
              setRoleActions([]);
              setSnackbar({
                severity: "error",
                description: data.message || "Rol action'ları alınamadı",
                isOpen: true,
              });
            }
          },
          onError: () => {
            setRoleActions([]);
            setSnackbar({
              severity: "error",
              description: "Rol action'ları alınırken bir hata oluştu",
              isOpen: true,
            });
          },
        }
      );
    } else {
      setRoleActions([]);
    }
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      { field: "id", headerName: "ID", width: 100 },
      { field: "microServiceName", headerName: "Microservice", width: 200 },
      { field: "controllerName", headerName: "Controller", width: 200 },
      { field: "actionName", headerName: "Action", width: 200 },
      { 
        renderCell: RenderStatus, 
        field: "canCall", 
        headerName: "Yetki", 
        width: 150,
        filterable: false,
        sortable: false,
      },
    ];
  }, []);

  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const getQueryParams = (filters: any) => {
    const queryParams: any = {};
    for (let key in filters) {
      if (filters[key]?.toString()?.length >= 1) {
        queryParams[key] = filters[key];
      }
    }
    return queryParams;
  };

  const previousQueryParams = React.useRef({});

  const handleFilterChange = debounce((filters: any) => {
    if (filters.clearFilter) {
      return setQueryOptions({});
    }

    const queryParams = getQueryParams(filters);
    const allValuesAtLeastThreeChars = Object.values(queryParams).every((value: any) => value?.toString().length >= 3);
    const atLeastOneValueThreeChars = Object.values(queryParams).some((value: any) => value?.toString().length >= 3);
    
    const previousKeysLength = Object.keys(previousQueryParams.current).length;
    const currentKeysLength = Object.keys(queryParams).length;

    if (JSON.stringify(previousQueryParams.current) === JSON.stringify(queryParams)) {
      return;
    }

    if ((allValuesAtLeastThreeChars || (atLeastOneValueThreeChars && previousKeysLength > currentKeysLength))) {
      setQueryOptions(queryParams);
    }

    previousQueryParams.current = queryParams;
  }, 500);

  const hasActiveFilters = Object?.keys(queryOptions)?.length > 0;

  const filteredRoleActions = useMemo(() => {
    if (!queryOptions || Object.keys(queryOptions).length === 0) {
      return roleActions;
    }

    return roleActions.filter((action: any) => {
      return Object.entries(queryOptions).every(([key, value]: [string, any]) => {
        const fieldValue = action[key]?.toString().toLowerCase() || "";
        const searchValue = value.toString().toLowerCase();
        return fieldValue.includes(searchValue);
      });
    });
  }, [roleActions, queryOptions]);

  const handleChangePagination = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const handleBulkDelete = () => {
    if (!selectedRoleId) {
      setSnackbar({
        severity: "error",
        description: "Lütfen bir rol seçiniz",
        isOpen: true,
      });
      return;
    }

    if (selectedActionIds.length === 0) {
      setSnackbar({
        severity: "warning",
        description: "Lütfen silinecek action'ları seçiniz",
        isOpen: true,
      });
      return;
    }
    
    const idsAsNumbers = selectedActionIds.map(id => Number(id));
    
    deleteBulkRoleActions(
      { ids: idsAsNumbers },
      {
        onSuccess: (data) => {
          if (data.isSuccess) {
            setSnackbar({
              severity: "success",
              description: data.message || "Seçilen action'lar başarıyla silindi",
              isOpen: true,
            });
            setSelectedActionIds([]);
            if (selectedRoleId) {
              getRoleActions(
                { roleId: selectedRoleId as number },
                {
                  onSuccess: (refetchData) => {
                    if (refetchData.isSuccess) {
                      setRoleActions(refetchData.data || []);
                    }
                  }
                }
              );
            }
          } else {
            setSnackbar({
              severity: "error",
              description: data.message || "Silme işlemi başarısız oldu",
              isOpen: true,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: "error",
            description: "Silme işlemi sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      }
    );
  };

  const isLoading = rolesLoading || getRoleActionsLoading || deleteLoading;
  const selectedRole = roleList.find((role: { label: string; value: number }) => role.value === selectedRoleId);

  return (
    <Box p={3}>
      {isLoading && <Loading />}
      
      <Typography variant="h6" gutterBottom>
        Rol Action Seçerek Silme
      </Typography>
      
      <Typography variant="body2" color="text.secondary" mb={3}>
        Bir rol seçin, action'ları görüntüleyin ve silmek istediğinizi seçin.
      </Typography>

      <Stack spacing={3}>
        <Box maxWidth={400}>
          <FormControl fullWidth>
            <InputLabel id="role-select-label">Rol Seçin</InputLabel>
            <Select
              labelId="role-select-label"
              value={selectedRoleId}
              label="Rol Seçin"
              onChange={handleRoleChange}
              disabled={isLoading}
            >
              {roleList.map((role: { label: string; value: number }) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {selectedRoleId && roleActions.length > 0 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {selectedRole?.label} Rolü Action'ları ({filteredRoleActions.length} adet)
              </Typography>
              <Stack direction="row" spacing={2}>
                <Chip 
                  label={`${selectedActionIds.length} seçildi`} 
                  color={selectedActionIds.length > 0 ? "primary" : "default"}
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleBulkDelete}
                  disabled={selectedActionIds.length === 0 || isLoading}
                  size="small"
                >
                  Seçilenleri Sil ({selectedActionIds.length})
                </Button>
              </Stack>
            </Box>

            <Table
              columnVisibilityConfigKey="BulkDeleteActionListingColumnVisibilityKey"
              handleFilterChange={handleFilterChange}
              hasActiveFilters={hasActiveFilters}
              paginationModel={paginationModel}
              onPaginationModelChange={handleChangePagination}
              paginationMode="client"
              rowCount={filteredRoleActions.length}
              sx={{ width: isDesktop ? 1308 : window.innerWidth - 50, height: 600 }}
              checkboxSelection
              onRowSelectionModelChange={setSelectedActionIds}
              rowSelectionModel={selectedActionIds}
              rows={filteredRoleActions}
              columns={columns}
              getRowClassName={(params: any) => {
                if (params.row.canCall === false) return 'pasive-row';
                return '';
              }}
            />

            {selectedActionIds.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>{selectedActionIds.length}</strong> adet action seçildi. 
                  "Seçilenleri Sil" butonuna tıkladığınızda bu action'lar kalıcı olarak silinecektir.
                </Typography>
              </Alert>
            )}
          </>
        )}

        {selectedRoleId && roleActions.length === 0 && (
          <Alert severity="info">
            <Typography variant="body2">
              <strong>{selectedRole?.label}</strong> rolünde henüz action bulunmamaktadır.
            </Typography>
          </Alert>
        )}
      </Stack>
    </Box>
  );
};